import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Button,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  ContentCut as ServicesIcon,
  Settings as SettingsIcon,
  EventNote as BookingsIcon,
  People as PeopleIcon,
  Group as EmployeesIcon,
  Logout as LogoutIcon,
  AttachMoney as FinanceIcon
} from '@mui/icons-material';
import adminStore from '../stores/AdminStore';

const drawerWidth = 240;

const AdminLayout = ({ user, onLogout }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.includes(path);
  };

  // Load shop settings on mount (will use cached version if already loaded)
  useEffect(() => {
    adminStore.loadShopSettings();
  }, []);

  // Build menu items based on user role
  const menuItems = [
    //{ text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    // Bookings - available for both admin and employee
    { text: 'Agendamentos', icon: <BookingsIcon />, path: '/bookings' },
    // Admin-only menus
    ...(user?.role === 'admin' ? [
      { text: 'Funcionários', icon: <EmployeesIcon />, path: '/employees' },
      { text: 'Serviços', icon: <ServicesIcon />, path: '/services' },
      { text: 'Usuários', icon: <PeopleIcon />, path: '/users' },
      { text: 'Financeiro', icon: <FinanceIcon />, path: '/finance' },
      { text: 'Configurações', icon: <SettingsIcon />, path: '/settings' }
    ] : [])
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed">
        <Toolbar>
          {/* Mobile: hamburger menu */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/Title */}
          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 600
            }}
          >
            {adminStore.shopName}
          </Typography>

          {/* Desktop: horizontal nav buttons */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, ml: 4 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  color="inherit"
                  sx={{
                    px: 2,
                    py: 1,
                    minWidth: 'auto',
                    opacity: isActive(item.path) ? 1 : 0.7,
                    fontWeight: isActive(item.path) ? 600 : 400,
                    borderBottom: '2px solid',
                    borderColor: isActive(item.path) ? '#fff' : 'transparent',
                    borderRadius: 0,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: '100%',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default observer(AdminLayout);
